import { Avatar, Button, makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { KeyboardArrowDown, Logout, StopCircle } from '@material-symbols-svg/react/rounded/w500';
import { useMemo, useState } from 'react';

import { firstNameOf, initialsOf } from '../common';
import { DiscardPendingPrompt } from '../components';
import { useCurrentUser, useMemberAvatar } from '../current-user';
import { useSettings } from '../settings';
import { palette, tokens } from '../theme';
import { usePending } from '../worklog';

export const ProfileChip: React.FC = () => {
    const classes = useStyles();
    const { user, actor, isActing, stopActing } = useCurrentUser();
    const { pending } = usePending();
    const { logout } = useSettings();
    const [open, setOpen] = useState(false);
    const [confirmingStop, setConfirmingStop] = useState(false);
    // The acted-for member's avatar comes from the shared per-email cache.
    const actingAvatarUrl = useMemberAvatar(isActing && user ? user.email : '');

    const initials = useMemo(() => (user ? initialsOf(user.name) : '?'), [user]);
    const firstName = useMemo(() => (user ? firstNameOf(user.name) : ''), [user]);

    if (!user) {
        return <div className={classes.placeholder} aria-hidden />;
    }

    const avatarUrl = isActing ? actingAvatarUrl : user.avatarObjectUrl;

    const handleLogout = (): void => {
        setOpen(false);
        stopActing();
        logout();
    };

    // Queued entries belong to the acted-for member; ask before dropping them.
    const requestStop = (): void => {
        setOpen(false);
        if (pending.length > 0) {
            setConfirmingStop(true);
        } else {
            stopActing();
        }
    };

    const renderAvatar = (size: 24 | 56) => (
        <span className={mergeClasses(classes.avatarWrap, isActing && classes.actingRing)}>
            <Avatar size={size} name={user.name} initials={initials} image={avatarUrl ? { src: avatarUrl } : undefined} />
        </span>
    );

    return (
        <div className={classes.wrap}>
            {isActing && (
                <button type="button" className={classes.stopButton} onClick={requestStop}>
                    <StopCircle className={classes.stopIcon} aria-hidden />
                    <span>Stop acting</span>
                </button>
            )}
            <Popover positioning="below-end" withArrow open={open} onOpenChange={(_, data) => setOpen(data.open)}>
                <PopoverTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        aria-label={isActing ? `Acting as ${user.name}, signed in as ${actor?.name ?? ''}` : `Signed in as ${user.name}`}
                        className={classes.trigger}
                        icon={renderAvatar(24)}
                        iconPosition="before"
                    >
                        <span className={mergeClasses(classes.firstName, isActing && classes.actingText)}>
                            {isActing ? `Acting as ${firstName}` : firstName}
                        </span>
                        <KeyboardArrowDown className={classes.chevron} aria-hidden />
                    </Button>
                </PopoverTrigger>
                <PopoverSurface className={classes.surface}>
                    <div className={classes.identity}>
                        {renderAvatar(56)}
                        <div className={classes.identityText}>
                            {isActing && <span className={classes.actingLabel}>Acting as</span>}
                            <span className={classes.displayName}>{user.name}</span>
                            <span className={classes.email}>{user.email}</span>
                        </div>
                    </div>
                    {isActing && actor && <p className={classes.signedInAs}>Signed in as {actor.name}</p>}
                    <div className={classes.divider} aria-hidden />
                    {isActing && (
                        <button type="button" className={mergeClasses(classes.menuItem, classes.actingText)} onClick={requestStop}>
                            <StopCircle className={mergeClasses(classes.menuIcon, classes.actingText)} />
                            <span>Stop acting as {firstName}</span>
                        </button>
                    )}
                    <button type="button" className={classes.menuItem} onClick={handleLogout}>
                        <Logout className={classes.menuIcon} />
                        <span>Sign out</span>
                    </button>
                </PopoverSurface>
            </Popover>
            {confirmingStop && (
                <DiscardPendingPrompt
                    title={`Stop acting as ${user.name}?`}
                    count={pending.length}
                    confirmLabel="Discard and stop"
                    onConfirm={() => {
                        setConfirmingStop(false);
                        stopActing();
                    }}
                    onCancel={() => setConfirmingStop(false)}
                />
            )}
        </div>
    );
};

const useStyles = makeStyles({
    wrap: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    trigger: {
        gap: tokens.spacingHorizontalS,
    },
    avatarWrap: {
        display: 'inline-flex',
        borderRadius: '50%',
    },
    actingRing: {
        boxShadow: `0 0 0 2px ${palette.actingAccent}`,
    },
    actingText: {
        color: palette.actingAccent,
    },
    actingLabel: {
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        color: palette.actingAccent,
    },
    signedInAs: {
        margin: 0,
        padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalM}`,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    stopButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '28px',
        padding: '0 10px',
        border: 'none',
        borderRadius: palette.pillRadius,
        backgroundColor: palette.removeBg,
        color: palette.actingAccent,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        cursor: 'pointer',
        '&:hover': {
            filter: 'brightness(0.96)',
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.actingAccent}`,
            outlineOffset: '2px',
        },
    },
    stopIcon: {
        width: '14px',
        height: '14px',
        fill: 'currentColor',
    },
    firstName: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    chevron: {
        width: '16px',
        height: '16px',
        color: tokens.colorNeutralForeground3,
    },
    placeholder: {
        width: '64px',
        height: '32px',
    },
    surface: {
        padding: 0,
        minWidth: '260px',
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    },
    identityText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    displayName: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    email: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    divider: {
        height: '1px',
        backgroundColor: palette.cardBorder,
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        textAlign: 'left',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: palette.panelMuted,
        },
    },
    menuIcon: {
        width: '18px',
        height: '18px',
        color: tokens.colorNeutralForeground2,
    },
});
